import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import type { TenantApp } from '@inloco/shared';

interface UseMapInitializationProps {
    mapContainerRef: React.RefObject<HTMLDivElement | null>;
    tileUrl: string;
    imageUrl?: string;
    mapBlobUrl: string | null;
    mapBounds?: TenantApp['mapConfig']['bounds'];
    center: { lt: number; lg: number };
    zoom: number;
    minZoom: number;
    maxZoom: number;
}

export function useMapInitialization({
    mapContainerRef,
    tileUrl,
    imageUrl,
    mapBlobUrl,
    mapBounds,
    center,
    zoom,
    minZoom,
    maxZoom
}: UseMapInitializationProps) {
    const mapRef = useRef<L.Map | null>(null);
    const imageOverlayRef = useRef<L.ImageOverlay | null>(null);
    const hasAppliedInitialViewRef = useRef(false);
    const [isMapReady, setIsMapReady] = useState(false);

    // Save initial center and zoom in ref (don't change after mount)
    const initialCenterRef = useRef(center);
    const initialZoomRef = useRef(zoom);
    const initialMinZoomRef = useRef(minZoom);
    const initialMaxZoomRef = useRef(maxZoom);

    // Initialize map
    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return;
        let isDisposed = false;

        // Create base map using initial values
        const map = L.map(mapContainerRef.current, {
            center: [initialCenterRef.current.lt, initialCenterRef.current.lg],
            zoom: initialZoomRef.current,
            attributionControl: false,
            minZoom: initialMinZoomRef.current,
            maxZoom: initialMaxZoomRef.current,
            zoomControl: false,
            dragging: true,
            touchZoom: false,
            scrollWheelZoom: false,
            doubleClickZoom: false,
            boxZoom: false,
            keyboard: false,
            inertia: false,
        });

        if (!imageUrl) {
            L.tileLayer(tileUrl, {
                maxZoom: 19,
                attribution: '© OpenStreetMap'
            }).addTo(map);
        } else {
            map.getContainer().style.backgroundColor = 'black';
        }

        mapRef.current = map;
        map.whenReady(() => {
            if (!isDisposed) {
                setIsMapReady(true);
            }
        });

        return () => {
            isDisposed = true;
            map.remove();
            mapRef.current = null;
            imageOverlayRef.current = null;
            hasAppliedInitialViewRef.current = false;
        };
    }, [tileUrl, imageUrl, mapContainerRef]);

    // Custom map image overlay
    useEffect(() => {
        if (!mapRef.current || !mapBounds) return;

        // Remove previous overlay
        if (imageOverlayRef.current) {
            imageOverlayRef.current.remove();
        }

        // Add new overlay
        const bounds = L.latLngBounds(
            [mapBounds.sw.lt, mapBounds.sw.lg],
            [mapBounds.ne.lt, mapBounds.ne.lg]
        );

        // Handle drag event
        // Prevents going outside the map
        const paddedBounds = bounds.pad(0.1);
        mapRef.current.setMaxBounds(paddedBounds);
        mapRef.current.off('drag');
        mapRef.current.on('drag', () => {
            if (mapRef.current) {
                if (!mapRef.current.getBounds().intersects(paddedBounds)) {
                    mapRef.current.panInsideBounds(paddedBounds, { animate: false });
                }
            }
        })

        if (mapBlobUrl) {
            // If there is a custom map, use it
            // Apply a black shadow effect class
            // To hide everything else
            const overlay = L.imageOverlay(mapBlobUrl, bounds, {
                interactive: false,
            }).addTo(mapRef.current);
            imageOverlayRef.current = overlay;
        }

        // Apply initial view only once to avoid overriding user auto-center.
        if (!hasAppliedInitialViewRef.current) {
            mapRef.current.setView([initialCenterRef.current.lt, initialCenterRef.current.lg], initialZoomRef.current, { animate: false });
            hasAppliedInitialViewRef.current = true;
        }

    }, [mapBlobUrl, mapBounds]);

    return { mapRef, isMapReady };
}
